package com.neurobs.ccabserver.administrator.contexts

import com.neurobs.ccabserver.administrator.components.NavFramework.{IconData, ItemType, MenuItemData, MenuSectionData}
import com.neurobs.react.{StateAttribute, StateContextBase}

case class MenuState
(
	alerts: Seq[MenuState.MenuItemType.Value] = Seq(),
	selection: Option[MenuState.MenuItemType.Value] = None,
	menuVisible: Boolean = true
) {
	import MenuState._

	def setAlert(t: MenuItemType.Value): MenuState = {
		if (alerts.contains(t)) this
		else copy(alerts = alerts :+ t)
	}

	def clearAlert(t: MenuItemType.Value): MenuState = {
		if (alerts.contains(t)) copy(alerts = alerts.filterNot(_ == t))
		else this
	}

	def setSelection(s: Option[MenuItemType.Value]): MenuState = {
		if (selection == s) this
		else copy(selection = s)
	}

	def setMenuVisible(visible: Boolean): MenuState = {
		if (visible == menuVisible) this
		else copy(menuVisible = visible)
	}
}

object MenuState {
	object MenuItemType extends Enumeration {
		val Examiner = Value("Examiner")
		val TestStations = Value("Test Stations")
		val Studies = Value("Studies")
		val Subjects = Value("Subjects")
		val Results = Value("Results")
		val Reviews = Value("Reviews")
		val Account = Value("Account")
		val Admin = Value("Admin")
		val Resources = Value("Resources")
	}

	def isAlert(t: MenuItemType.Value): Boolean = t == MenuItemType.Examiner

	private val itemIcons = Map(
		MenuItemType.Examiner -> IconData(className = Some("fa-solid fa-microscope")),
		MenuItemType.TestStations -> IconData(className = Some("fa-solid fa-laptop")),
		MenuItemType.Studies -> IconData(className = Some("fa-solid fa-flask-vial")),
		MenuItemType.Subjects -> IconData(className = Some("fa-solid fa-users-line")),
		MenuItemType.Results -> IconData(className = Some("fa-solid fa-square-poll-vertical"), sizeScaleFactor = Some(1.28)),
		MenuItemType.Reviews -> IconData(className = Some("fa-solid fa-magnifying-glass-chart"), sizeScaleFactor = Some(1.28)),
		MenuItemType.Account -> IconData(className = Some("fa-solid fa-gear"), sizeScaleFactor = Some(1.14)),
		MenuItemType.Admin -> IconData(className = Some("fa-solid fa-screwdriver-wrench")),
		MenuItemType.Resources -> IconData(className = Some("fa-solid fa-book"))
	)

	private def makeItem(t: MenuItemType.Value): MenuItemData = MenuItemData(
		itemType = if (isAlert(t)) ItemType.Alert else ItemType.Item,
		icon = itemIcons(t),
		caption = t.toString
	)

	val baseMenu: Seq[MenuSectionData] = Seq(
		MenuSectionData(
			items = Seq(
				makeItem(MenuItemType.Examiner),
				makeItem(MenuItemType.TestStations)
			)
		),
		MenuSectionData(
			items = Seq(
				makeItem(MenuItemType.Studies),
				makeItem(MenuItemType.Subjects),
				makeItem(MenuItemType.Results),
				makeItem(MenuItemType.Reviews),
				makeItem(MenuItemType.Account)
			)
		),
		MenuSectionData(
			items = Seq(
				makeItem(MenuItemType.Admin)
			)
		),
		MenuSectionData(
			items = Seq(
				makeItem(MenuItemType.Resources)
			)
		)
	)
}

class MenuContext(state: StateAttribute[MenuState]) {
	import MenuState._

	private def set(newState: MenuState): Unit = {
		if (newState ne value) {
			state.setValue(newState)
		}
	}

	def value: MenuState = state.value

	def setExaminerStatus(on: Boolean): Unit = {
		if (on) set(value.setAlert(MenuItemType.Examiner).setSelection(None).setMenuVisible(false))
		else set(value.clearAlert(MenuItemType.Examiner).setSelection(Some(MenuItemType.TestStations)).setMenuVisible(true))
	}

	def setAlert(t : MenuItemType.Value): Unit = {
		set(value.setAlert(t))
	}

	def clearAlert(t: MenuItemType.Value): Unit = {
		set(value.clearAlert(t))
	}

	def setSelection(s: Option[MenuItemType.Value]): Unit = {
		set(value.setSelection(s))
	}

	def setMenuVisible(visible: Boolean): Unit = {
		set(value.setMenuVisible(visible))
	}

	def set(selection: Option[MenuItemType.Value], menuVisible: Boolean): Unit = {
		set(value.setSelection(selection).setMenuVisible(menuVisible))
	}
}

object MenuContext extends StateContextBase[MenuContext, MenuState] {
	override protected def defaultStateValue(): MenuState = MenuState()

	override protected def makeState(a: StateAttribute[MenuState]): MenuContext = new MenuContext(a)
}